import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { authAPI } from '../../services/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const CustomerAuth = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await authAPI.requestMagicLink(email);
      setSent(true);
      toast.success('Magic link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFF0] flex flex-col">
      {/* Header */}
      <header className="px-6 py-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#4A154B] transition-colors"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md animate-fadeIn">
          {/* Logo */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gradient-primary mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-serif text-3xl text-[#4A154B] mb-2">Welcome Back</h1>
            <p className="text-gray-600">Sign in to access your rewards</p>
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} className="glass rounded-2xl p-8">
              <div className="space-y-6">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-12 h-12"
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    We'll send you a magic link to sign in instantly
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full h-12 btn-primary"
                  data-testid="send-magic-link-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Magic Link'
                  )}
                </Button>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  New to Salon O? Enter your email to create an account.
                </p>
              </div>
            </form>
          ) : (
            <div className="glass rounded-2xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#8FBC8F]/20 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#8FBC8F]" />
              </div>
              <h2 className="font-serif text-2xl text-[#4A154B] mb-3">Check Your Email</h2>
              <p className="text-gray-600 mb-6">
                We've sent a magic link to<br />
                <span className="font-medium text-[#4A154B]">{email}</span>
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Click the link in the email to sign in. The link expires in 15 minutes.
              </p>
              <button
                onClick={() => setSent(false)}
                className="text-[#C9A689] hover:text-[#4A154B] font-medium transition-colors"
                data-testid="try-different-email-btn"
              >
                Try a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerAuth;
